import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { Back, Search, MyLocation } from '@watersport/ui-react-icons'
import { Button, Input } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { useSearch } from 'contexts/SearchProvider'
import { cn } from 'features/common/helpers/cn'
import { useLocations } from 'features/common/hooks/useLocations'
import { Location, SearchScope } from 'features/common/types'

export interface AutoCompletePlaceProps {
  placeName?: string
  center: google.maps.LatLngLiteral | undefined
  onPlaceSelected: (
    place: google.maps.LatLngLiteral | undefined,
    placeName: string | undefined,
  ) => void
  onClickMyLocation?: () => void
  onClose?: () => void
  onClearSearch?: () => void
  show: boolean
}

export interface AutoCompletePlaceInterface {
  setInitialScope: (scope: SearchScope) => void
}

const AutoCompletePlace: React.ForwardRefRenderFunction<
  AutoCompletePlaceInterface,
  AutoCompletePlaceProps
> = ({ placeName, onClose, onPlaceSelected, onClickMyLocation, onClearSearch, show }, ref) => {
  const map = useMap()
  const places = useMapsLibrary('places')
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>()
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null)
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null)
  const [predictionResults, setPredictionResults] = useState<
    Array<google.maps.places.AutocompletePrediction>
  >([])
  const [value, setValue] = useState<string>('')
  const [showDropDown, setShowDropDown] = useState<boolean>(false)
  const { locations, enableNearby } = useLocations()
  const { myLocation, requestLocation, showModal } = useSearch()

  useEffect(() => {
    enableNearby(false)
  }, [enableNearby])

  useEffect(() => {
    setValue(placeName ?? '')
  }, [placeName])

  useEffect(() => {
    if (places && map) {
      setAutocompleteService(new places.AutocompleteService())
      setPlacesService(new places.PlacesService(map))
      setSessionToken(new places.AutocompleteSessionToken())
    }
    return () => setAutocompleteService(null)
  }, [map, places])

  const fetchPredictions = useCallback(
    async (inputValue: string) => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([])
        return
      }

      const request = { input: inputValue, sessionToken }
      const response = await autocompleteService.getPlacePredictions(request)

      setPredictionResults(response.predictions)
    },
    [autocompleteService, sessionToken],
  )

  const onChange = useCallback(
    (v: string) => {
      setValue(v)
      fetchPredictions(value).then()
    },
    [fetchPredictions, value],
  )

  const handleSuggestionClick = useCallback(
    (placeId: string) => {
      if (!places) return

      const detailRequestOptions = {
        placeId,
        fields: ['geometry', 'name', 'formatted_address'],
        sessionToken,
      }

      const detailsRequestCallback = (placeDetails: google.maps.places.PlaceResult | null) => {
        if (placeDetails?.geometry?.location?.lat() && placeDetails?.geometry?.location?.lng()) {
          const coordinate = {
            lat: placeDetails?.geometry?.location?.lat(),
            lng: placeDetails?.geometry?.location?.lng(),
          }
          onPlaceSelected(coordinate, placeDetails?.formatted_address ?? '')
          if (map) map.setCenter(coordinate)
        }
        setPredictionResults([])
        setValue(placeDetails?.formatted_address ?? '')
        setSessionToken(new places.AutocompleteSessionToken())
        setShowDropDown(false)
      }

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback)
    },
    [map, onPlaceSelected, places, placesService, sessionToken],
  )

  const onClickAroundMyLocation = useCallback(async () => {
    setShowDropDown(false)
    if (myLocation) {
      onPlaceSelected(myLocation, undefined)
      if (map) map.setCenter(myLocation)
      if (onClickMyLocation) onClickMyLocation()
    } else {
      const status = await showModal()
      if (status) {
        requestLocation()
      }
    }
  }, [myLocation, onPlaceSelected, map, onClickMyLocation, showModal, requestLocation])

  const onClickPopularItem = useCallback(
    (item: Location) => {
      if (item.coordinates) {
        const [lng, lat] = item.coordinates
        const coordinate = { lat, lng }
        onPlaceSelected(coordinate, undefined)
        setValue(item.name)
        setShowDropDown(false)
        if (map) map.setCenter(coordinate)
      }
    },
    [map, onPlaceSelected],
  )

  const onClickBack = useCallback(() => {
    if (showDropDown) {
      setShowDropDown(false)
      return
    }
    if (onClose) onClose()
  }, [onClose, showDropDown])

  const onClear = useCallback(() => {
    setValue('')
    if (onClearSearch) onClearSearch()
  }, [onClearSearch])

  const showMostPopular = useMemo(
    () => !value || predictionResults.length === 0,
    [predictionResults, value],
  )

  useEffect(() => {
    if (value && showDropDown) {
      fetchPredictions(value).then()
    }
  }, [fetchPredictions, showDropDown, value])

  useEffect(() => {
    if (map && myLocation) map.setCenter(myLocation)
  }, [map, myLocation])

  useImperativeHandle(
    ref,
    () => ({
      setInitialScope(scope: SearchScope) {
        if (map && scope.center) map.setCenter(scope.center)
        setValue(scope.placeName ?? '')
      },
    }),
    [map],
  )

  return (
    <div
      className={cn('absolute top-0 left-0 right-0 flex flex-col', showDropDown && 'h-full')}
      data-testid="autocomplete-place-container"
    >
      <div
        className={cn(
          'flex flex-row items-center justify-center bg-blue-900 px-5 py-4 space-y-0 gap-2.5 transition-transform duration-300',
          show ? 'translate-y-0' : '-translate-y-full',
        )}
        data-testid="autocomplete-header"
      >
        <div>
          <Button
            className={cn(
              'w-[52px] h-[52px] bg-blue-600 rounded-lg shadow-md cursor-pointer hover:bg-blue-600',
            )}
            variant={'default'}
            text={<Back width={24} height={24} />}
            onClick={onClickBack}
            data-testid="back-button"
          />
        </div>
        <div className="flex-1">
          <Input
            className="rounded-xl h-[52px]"
            value={value}
            onChange={onChange}
            placeholder={'Where?'}
            icon={<Search width={24} height={24} className="text-blue-700" />}
            onClear={onClear}
            onFocus={() => setShowDropDown(true)}
            data-testid="search-input"
          />
        </div>
      </div>
      {showDropDown && (
        <div className="bg-white flex-1 flex flex-col overflow-hidden z-50">
          <div
            className="flex justify-center items-center px-6 py-4 gap-3 hover: cursor-pointer"
            onClick={onClickAroundMyLocation}
            data-testid="my-location-option"
          >
            <MyLocation width={24} height={24} className="text-blue-700" />
            <span className="flex-1 text-[15px]">{'Around your location'}</span>
          </div>
          <p className="flex items-center justify-center text-blue-900 text-sm h-10 bg-blue-100">
            {'RESULTS'}
          </p>
          <div className="flex-1 overflow-y-auto">
            {showMostPopular &&
              locations.map((location, index) => (
                <div
                  className="px-6 py-4 border-b border-blue-200 leading-none"
                  key={index}
                  onClick={() => onClickPopularItem(location)}
                  data-testid={`popular-item-${index}`}
                >
                  <h1 className="text-[15px] font-semibold">{location.name}</h1>
                  <span className="text-xs font-normal text-gray-600">
                    {location.city
                      ? `${location.city}, ${location.country?.name}`
                      : location.country?.name}
                  </span>
                </div>
              ))}
            {!showMostPopular &&
              predictionResults.map((item, index) => (
                <div
                  className="px-6 py-4 border-b border-blue-200 leading-none"
                  key={index}
                  onClick={() => handleSuggestionClick(item.place_id)}
                  data-testid={`prediction-item-${index}`}
                >
                  <h1 className="text-[15px] font-semibold">
                    {item.structured_formatting.main_text}
                  </h1>
                  <span className="text-xs font-normal text-gray-600">{item.description}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default React.forwardRef(AutoCompletePlace)
